import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { api, cdnImg, money } from "@/lib/api";
import { Fireworks } from "@/components/Fireworks";
import { DiwaliCountdown } from "@/components/DiwaliCountdown";

// Twinkling gold sparks scattered across the night sky backdrop.
const SPARKS = [
  [8, 18, 0], [18, 62, 0.8], [30, 12, 1.6], [42, 78, 0.4], [55, 8, 2.1],
  [66, 55, 1.2], [78, 22, 0.6], [88, 66, 1.9], [94, 14, 0.2], [12, 84, 1.4],
];

/** Homepage takeover hero for the Diwali Sale — grand fireworks on open. */
export const DiwaliHero = () => {
  const [shots, setShots] = useState([]);
  const [bridalLive, setBridalLive] = useState(false);

  useEffect(() => {
    api.get("/sale/current?summary=1")
      .then((r) => {
        const all = [...(r.data.festive || []), ...(r.data.bridal || [])];
        setShots(all.filter((p) => p.images?.length).slice(0, 3));
        setBridalLive((r.data.bridal || []).length > 0);
      })
      .catch(() => {});
  }, []);

  return (
    <section
      data-testid="diwali-hero"
      className="relative overflow-hidden text-[#FFFFFF]"
      style={{ background: "linear-gradient(155deg, #3D0A28 0%, #160410 55%, #0A0207 100%)" }}
    >
      {/* ambient gold glows */}
      <div className="absolute -top-32 right-[10%] w-[28rem] h-[28rem] rounded-full bg-[#F5C542]/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-[5%] w-[22rem] h-[22rem] rounded-full bg-[#FF5D8F]/10 blur-3xl pointer-events-none" />

      {/* twinkling sparks */}
      {SPARKS.map(([x, y, d], i) => (
        <span
          key={i}
          className="absolute w-1 h-1 rounded-full bg-[#F5C542] animate-pulse pointer-events-none"
          style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${d}s`, animationDuration: "2.6s" }}
        />
      ))}

      <Fireworks duration={7000} />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 lg:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-xl"
        >
          <div className="overline text-[#F5C542] mb-4 flex items-center gap-2">
            <Flame size={13} strokeWidth={1.8} /> Diwali Week · Festival of Lights
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
            End of Season <span className="italic text-[#F5C542]">Sale</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#FFFFFF]/80 font-body leading-relaxed">
            Up to <b className="text-[#F5C542] font-semibold">70% off</b> festive styles · Bridal &amp; guest lehengas at a
            flat <b className="text-[#F5C542] font-semibold">$399</b> — in-store in Schaumburg &amp; online across the USA.
            When they&apos;re gone, they&apos;re gone.
          </p>

          <div className="mt-8" data-testid="diwali-hero-countdown">
            <div className="text-[0.62rem] font-mono tracking-[0.2em] uppercase text-[#FFFFFF]/60 mb-3">Sale ends Diwali night</div>
            <DiwaliCountdown />
          </div>

          <div className="flex flex-wrap gap-3 mt-9">
            <a
              href="/festive-sale"
              data-testid="diwali-hero-cta-sale"
              className="inline-flex items-center gap-2 bg-[#F5C542] text-[#160410] px-7 py-4 text-[0.72rem] font-mono tracking-[0.18em] uppercase font-semibold hover:bg-[#FFFFFF] transition-colors duration-300"
            >
              Shop the Sale <ArrowRight size={14} />
            </a>
            {bridalLive && (
              <a
                href="/festive-sale#bridal"
                data-testid="diwali-hero-cta-bridal"
                className="inline-flex items-center gap-2 border border-[#FF5D8F]/60 text-[#FF5D8F] px-7 py-4 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:bg-[#FF5D8F] hover:text-[#160410] transition-colors duration-300"
              >
                New · Bridal @ $399 <ArrowRight size={14} />
              </a>
            )}
          </div>

          <p className="mt-6 text-[0.7rem] font-mono tracking-[0.14em] uppercase text-[#FFFFFF]/50">
            Free U.S. shipping over $75 · Easy 14-day returns
          </p>
        </motion.div>

        {/* Product collage — real pieces from the sale */}
        {shots.length > 0 && (
          <div className="hidden lg:flex justify-center items-end gap-5" data-testid="diwali-hero-collage">
            {shots.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 40, rotate: 0 }}
                animate={{ opacity: 1, y: 0, rotate: i === 1 ? 0 : i === 0 ? -4 : 4 }}
                transition={{ duration: 0.9, delay: 0.25 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                className={i === 1 ? "mb-8" : ""}
              >
                <Link
                  to={`/product/${p.slug}`}
                  data-testid={`diwali-hero-piece-${p.slug}`}
                  className="group block w-44 xl:w-52 border border-[#F5C542]/30 bg-[#FFFFFF]/5 p-2 hover:border-[#F5C542] transition-colors duration-300"
                >
                  <div className="aspect-[3/4] overflow-hidden bg-[#EFEFEA]">
                    <img
                      src={cdnImg(p.images[0], 500)}
                      alt={p.title}
                      loading={i === 1 ? "eager" : "lazy"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="pt-2.5 pb-1 px-1 flex items-center justify-between gap-2">
                    <span className="text-[0.62rem] font-body text-[#FFFFFF]/75 truncate">{p.title}</span>
                    <span className="text-[0.72rem] font-mono text-[#F5C542] font-semibold shrink-0">{money(p.price)}</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
